// ============================================================
// TERROR TCG — VOID GRIMOIRE THEME v3
// Black holes of purple light, red rivers of mist,
// gold filigree on the edge of oblivion
// ============================================================

// ── Board / card / field colors — DO NOT use in UI chrome ────
export const SD = {
  void0: "#000001",
  void1: "#03020c",
  void2: "#07051a",
  void3: "#0d0a28",
  void4: "#1a1540",

  starWhite: "#f0e6d0",
  starSilver: "#d4c9b8",
  starDim: "#9a8a7a",

  gold: "#c9a84c",
  goldBright: "#f0d060",
  goldDim: "#4a3c18",

  purple: "#6d28d9",
  purpleLight: "#a78bfa",
  purpleDim: "#2d1b69",

  teal: "#0e7490",
  tealLight: "#22d3ee",
  tealDim: "#0a2830",

  red: "#dc2626",
  redDim: "#7f1d1d",
  redMist: "#991b1b",

  blue: "#3b82f6",
  blueDim: "#1e3a8a",

  fontDisplay: "'Cinzel', 'Palatino Linotype', serif",
  fontMono: "'Courier New', Courier, monospace",
};

// ── Grimoire Manuscript Palette — use for ALL UI chrome ──────
// Aged parchment, iron gall ink, candlelight, wax seals, vellum
export const GM = {
  // Page backgrounds — aged vellum, dark ink-stained
  page:          "#0f0c07",       // deepest ink-soaked vellum
  pageWarm:      "#1a1509",       // aged leather cover
  pageMid:       "#241c0c",       // worn parchment mid
  pageLight:     "#2e2410",       // candlelit page
  pageBorder:    "#3d2e14",       // manuscript margin line

  // Ink colors
  inkGold:       "#c8963c",       // iron gall gold — primary accent
  inkGoldBright: "#e8b84b",       // fresh gold illumination
  inkGoldDim:    "#8a6520",       // faded annotation
  inkBrown:      "#8a6a30",       // worn copper border
  inkRed:        "#8b2020",       // vermillion rubric

  // Parchment text
  vellum:        "#e8d5a3",       // warm candlelight cream — main text
  vellumDim:     "#b89a5a",       // aged gold ink — secondary
  vellumFaint:   "#7a6040",       // water-damaged — muted

  // Decorative
  ruling:        "rgba(139,106,48,0.18)", // faint ruling lines

  font:  "'Cinzel', 'Palatino Linotype', serif",
  fontD: "'Cinzel Decorative', 'Palatino Linotype', serif",
};

// ── Card type colors — semantic, unchanged ────────────────────
export const TYPE_COLORS_SD = {
  "Escendant": "#f87171",
  "Tome":      "#22d3ee",
  "Primera":   "#fbbf24",
  "U'hlti":   "#c084fc",
  "Affinity":  "#f0d060",
  "Prophecy":  "#818cf8",
  "Curio":     "#fb923c",
  "Escling":   "#a8896a",
};

export const ILK_COLORS_SD = {
  "Space":   "#22d3ee",
  "Phantom": "#c084fc",
  "Dragon":  "#f87171",
  "Time":    "#fbbf24",
};

// ── Board background — for Shatterfield / walkable rooms ─────
export const STARFIELD_BG = `
  radial-gradient(ellipse at 25% 25%, rgba(109,40,217,0.35) 0%, rgba(109,40,217,0.08) 35%, transparent 60%),
  radial-gradient(ellipse at 75% 70%, rgba(153,27,27,0.28) 0%, rgba(153,27,27,0.06) 40%, transparent 65%),
  radial-gradient(ellipse at 60% 15%, rgba(109,40,217,0.20) 0%, transparent 45%),
  radial-gradient(ellipse at 15% 75%, rgba(220,38,38,0.18) 0%, transparent 40%),
  radial-gradient(ellipse at 85% 45%, rgba(14,116,144,0.12) 0%, transparent 35%),
  radial-gradient(ellipse at 50% 50%, rgba(45,27,105,0.25) 0%, transparent 70%),
  #000001
`;

// ── Manuscript page background — for all UI screens ──────────
export const MANUSCRIPT_BG = [
  "repeating-linear-gradient(0deg, transparent, transparent 27px, rgba(139,106,48,0.05) 27px, rgba(139,106,48,0.05) 28px)",
  "radial-gradient(ellipse at 25% 15%, rgba(120,80,20,0.18) 0%, transparent 55%)",
  "radial-gradient(ellipse at 75% 85%, rgba(100,60,15,0.15) 0%, transparent 55%)",
  "#0f0c07",
].join(", ");

// ── Glow helpers ──────────────────────────────────────────────
// shimmerBorder — for BOARD elements (cards, holos, combat UI)
export const shimmerBorder = (color = "#c9a84c") =>
  `0 0 0 1px ${color}99, 0 0 12px ${color}55, 0 0 24px ${color}28`;

// inkGlow — for MANUSCRIPT chrome (panels, buttons, badges)
export const inkGlow = (color = "#c8963c") =>
  `0 0 16px ${color}33, inset 0 1px 0 ${color}22`;

// ════════════════════════════════════════════════════════════════════
// ⭐ UI — THE CHROME LAYER (added 2026-09-14, the cohesion pass)
// ────────────────────────────────────────────────────────────────────
// An audit of every player-facing surface found THREE parallel design
// languages and, underneath them, a product with:
//
//     20 corner radii · 32 font sizes · 13 golds · 9 violets ·
//     11 mid-greys · 11 greens · 5 reds · and tracking measured in px
//     on half the screens and em on the other half.
//
// None of that was anybody's decision. It is what happens when each new
// screen names its own colours, and it is exactly why the pier, the
// Hall, the quest board and the Minerium read as four products.
//
// ⚑ THIS IS A NAMING LAYER, NOT A NEW PALETTE. Every value below
// already existed in `SD` or `GM` — they just had no single name, so
// nine files invented nine near-misses of each. `SD`, `GM`, `MK` and
// `MARK_ACCENTS` are all untouched and still exported.
//
// ⚠ THE ONE DIM. `UI.dim` is 6.11:1 on `UI.panel`. The eleven separate
// hand-rolled greys it replaces measured between 2.11:1 and 3.60:1 —
// every contrast failure in the audit was in a file that had invented
// its own. If you find yourself typing a grey, you want this one.
//
// ⚠⚠ THE RATIOS BELOW ARE MEASURED, NOT ESTIMATED, and they are
// re-measurable: `tools/contrast.mjs` composites each token against
// `UI.panel` over the canvas ground and prints the WCAG figure. The
// first draft of this block carried ratios somebody typed from memory —
// they were not merely stale, they were mutually impossible (the
// text:dim relationship is fixed at 2.30x by the colours themselves and
// the comments claimed 1.97x). A theme file that lies about its own
// numbers is worse than one that states none, because the next person
// trusts it. Run the tool; do not type a number here.
//
// ⚠ `UI.faint` IS DECORATIVE ONLY. Never real copy. That is the rule
// the eleven greys were each breaking.
//
// ⚑ THE MARK IS A DELIBERATE SECOND VOICE and is NOT converted: Mark.js,
// MarkEditor.js, IEFriends.js and PierMarketplace.js are the portable
// ILLEGALEMPIRE.SITE identity chrome (Bodoni, notched corners, obsidian)
// and they say so in their own headers. Once Cinzel actually loads,
// "pier gold serif" against "Empire Bodoni obsidian" reads as a register
// change rather than as two teams.
// ════════════════════════════════════════════════════════════════════
export const UI = {
  // ── surfaces ──────────────────────────────────────────────────────
  panel:      "rgba(5,3,20,0.96)",   // the one overlay ground
  panelSolid: "#0b0716",
  scrim:      "rgba(2,0,16,0.86)",
  edge:       "#241f31",             // the one hairline
  edgeHot:    "#3a3050",

  // ── text ──────────────────────────────────────────────────────────
  text:  GM.vellum,                  // #e8d5a3 — 14.07:1 on panel
  dim:   SD.starDim,                 // #9a8a7a —  6.11:1 on panel. THE ONLY DIM.
  faint: GM.vellumFaint,             // #7a6040 —  3.47:1 · DECORATIVE ONLY, never copy

  // ── accent · one of each ──────────────────────────────────────────
  gold:   SD.gold,                   // #c9a84c —  8.93:1
  goldHi: SD.goldBright,             // #f0d060 — 13.50:1
  goldLo: GM.inkGoldDim,             // #8a6520 — borders and rules only
  violet:   SD.purpleLight,          // #a78bfa —  7.50:1
  violetLo: SD.purpleDim,            // #2d1b69 — a ground, never text
  // ⚠ `ok` was already hardcoded in four files before this layer existed
  // (CrestExchange, MineriumScreen, UnstablePierre, Minerium3D) — it is
  // named here, not invented here.
  ok:  "#9ad46a",                    // 11.66:1 — the green the theme never NAMED
  err: "#f0a0a0",                    //  9.95:1 — retires five reds

  // ── geometry · THREE radii, not twenty ────────────────────────────
  rSm: 5, rMd: 8, rLg: 16, rPill: 999,

  // ── type · em everywhere, one scale ───────────────────────────────
  font: SD.fontDisplay,
  mono: SD.fontMono,
  size:  { micro: 9, label: 11, body: 13, head: 18, title: 28 },
  track: { micro: ".22em", label: ".14em", body: ".02em", title: ".18em" },
};

// A modal / overlay shell.
export const uiPanel = (accent = UI.gold) => ({
  background: UI.panel,
  border: `1px solid ${accent}55`,
  borderRadius: UI.rLg,
  boxShadow: `0 20px 60px #000c, 0 0 40px ${accent}22`,
  fontFamily: UI.font,
  color: UI.text,
});

// A readout chip — currency, status, count.
export const uiChip = (accent = UI.gold) => ({
  display: "flex", alignItems: "center", gap: 6,
  background: UI.panel,
  border: `1px solid ${accent}88`,
  borderRadius: UI.rPill,
  padding: "5px 14px",
  fontFamily: UI.font,
  color: UI.text,
  boxShadow: `0 0 12px ${accent}33, inset 0 1px 0 ${accent}30`,
});

// A button. `on` is the selected state, not a hover.
export const uiBtn = (accent = UI.gold, { on = false, disabled = false } = {}) => ({
  fontFamily: UI.font,
  fontSize: UI.size.label,
  letterSpacing: UI.track.label,
  padding: "7px 13px",
  borderRadius: UI.rMd,
  cursor: disabled ? "default" : "pointer",
  background: disabled ? "transparent" : on ? `${accent}22` : "rgba(6,5,12,.82)",
  color: disabled ? UI.dim : on ? accent : UI.text,
  border: `1px solid ${disabled ? UI.edge : on ? `${accent}77` : UI.edge}`,
  whiteSpace: "nowrap",
});

// A micro-label — the small tracked capitals above a group.
export const uiLabel = {
  fontFamily: UI.font,
  fontSize: UI.size.micro,
  letterSpacing: UI.track.micro,
  color: UI.dim,
  textTransform: "uppercase",
};
